import { APP_WEB_DOMAIN_LABELS } from '@/lib/constants/app';
import type { AppCfgsDto } from '@/lib/types/App';
import type { AppV1Browse, AppV1Item, AppV1Row } from '@/lib/types/AppV1';

/** The shell's row type for the spotlight billboard (AppV1Assembler.composeRows). */
export const HERO_BILLBOARD_ROW_TYPE = 'hero_billboard';

export type HeroStyle = 'card' | 'immersive';

/** ADR-0013 logo slot at FHD: at most 500 wide x 160 tall at the 96 margin, bottom on the title's bottom. */
export const HERO_LOGO_BOX_W = 500;
export const HERO_LOGO_BOX_H = 160;

/** Pure mapping logic for the in-dashboard TV preview (vitest-covered). */
export class AppPreviewUtils {
  /**
   * The app's public domain — the /app/v1 tenancy key. Custom domain wins,
   * matching the server's resolution order; null when the app has no web row
   * (no tenancy anchor) or an unknown shared domain.
   */
  static appDomain(app: AppCfgsDto): string | null {
    const web = app.web;
    if (!web) return null;
    if (web.core.customDomain) return web.core.customDomain;
    const label = APP_WEB_DOMAIN_LABELS[web.core.sharedDomainId];
    return label && web.core.customName ? `${web.core.customName}.${label}` : null;
  }

  /** Artwork URL for a row's aspect, with the contract's card fallback chain. */
  static artworkUrl(item: AppV1Item, itemAspect: string): string | null {
    const a = item.artwork;
    switch (itemAspect) {
      case '2x3':
        return a.poster2x3 ?? a.card ?? null;
      case '1x1':
        return a.square ?? a.card ?? null;
      case 'banner':
        return a.banner ?? a.card ?? null;
      default:
        return a.card ?? a.poster2x3 ?? null;
    }
  }

  /** Billboard art: the derived hero, else the card (rokucore ArtworkUriFor). */
  static heroArtworkUrl(item: AppV1Item): string | null {
    return item.artwork.hero ?? item.artwork.card ?? null;
  }

  /**
   * Ask the image route for a display-sized rendition (rokucore SizedArtworkUri):
   * only /app/img/ URLs understand `w`; `raw` bypasses the derived hero crop so
   * the customer's complete composition renders whole (the card style).
   */
  static sizedArtworkUrl(url: string, width: number, raw = false): string {
    if (!url.includes('/app/img/')) return url;
    const params = raw ? `w=${width}&src=raw` : `w=${width}`;
    return url + (url.includes('?') ? '&' : '?') + params;
  }

  /** Server-selected treatment; card is the unknown-value fallback (Rows.bs). */
  static heroStyle(row: Pick<AppV1Row, 'heroStyle'>): HeroStyle {
    return row.heroStyle === 'immersive' ? 'immersive' : 'card';
  }

  /**
   * Title slot rule (design ADR-0013): the series' `artwork.logo` REPLACES
   * the typed title once the image has loaded; the text title renders until
   * then and whenever the logo is absent or failed. Never both.
   */
  static heroTitleSlot(item: Pick<AppV1Item, 'artwork'>, logoLoaded: boolean): 'logo' | 'text' {
    return item.artwork.logo && logoLoaded ? 'logo' : 'text';
  }

  /** Description cap while a logo shows (2 lines keep the CTA inside the 648 band; 3 under text). */
  static heroDescriptionLines(slot: 'logo' | 'text'): number {
    return slot === 'logo' ? 2 : 3;
  }

  /**
   * Rendered logo size in FHD pixels: scaled to fit the slot's 500 x 160 box
   * and never enlarged past the asset's native size (a 300 x 120 mark stays
   * 300 x 120). The shells apply the same rule; the box is the series
   * header's ratified geometry.
   */
  static heroLogoSize(naturalW: number, naturalH: number): { w: number; h: number } {
    if (naturalW <= 0 || naturalH <= 0) return { w: 0, h: 0 };
    const scale = Math.min(HERO_LOGO_BOX_W / naturalW, HERO_LOGO_BOX_H / naturalH, 1);
    return { w: Math.round(naturalW * scale), h: Math.round(naturalH * scale) };
  }

  /** The spotlight row the shell diverts to the billboard (first with items). */
  static heroRow(rows: AppV1Row[]): AppV1Row | null {
    return (
      rows.find((row) => row.rowType === HERO_BILLBOARD_ROW_TYPE && row.items.length > 0) ??
      null
    );
  }

  /** Everything the shell renders as rails: non-empty rows minus the billboard. */
  static railRows(rows: AppV1Row[]): AppV1Row[] {
    return rows.filter((row) => row.rowType !== HERO_BILLBOARD_ROW_TYPE && row.items.length > 0);
  }

  /**
   * Rows worth rendering in a static preview: viewer-scoped rows (continue
   * watching) and off-air live rails arrive empty without a viewer session —
   * drop anything with no items rather than paint empty shelves.
   */
  static visibleRows(browse: AppV1Browse): AppV1Row[] {
    return browse.rows.filter((row) => row.items.length > 0);
  }

  /**
   * Tile art size in FHD pixels per row aspect (rokucore Rows.bs CardSizeFor);
   * the shell's 16:9 default is 320x180.
   */
  static cardSizeFhd(itemAspect: string): { w: number; h: number } {
    switch (itemAspect) {
      case '2x3':
        return { w: 200, h: 300 };
      case '1x1':
        return { w: 180, h: 180 };
      case 'banner':
        return { w: 504, h: 180 };
      default:
        return { w: 320, h: 180 };
    }
  }

  /** Row pitch inputs (rokucore HomeView): art height + below-card title block + 24px pad. */
  static rowHeightFhd(row: Pick<AppV1Row, 'itemAspect' | 'titleTreatment'>): number {
    const size = AppPreviewUtils.cardSizeFhd(row.itemAspect);
    const labelSpace = row.titleTreatment === 'below_card' ? 104 : 0;
    return size.h + labelSpace + 24;
  }

  /**
   * Hero-state rows origin — port of rokucore Rows.bs HeroRowsY (the
   * "second-row peek"): with 2+ rows the origin drops so row 2's top lands at
   * 1004, clamped to [672, 700]; single rows and rows too tall to peek keep 700.
   */
  static heroRowsY(rowHeights: number[], rowCount: number): number {
    const DEFAULT = 700;
    const MIN = 672;
    const SPACING = 40;
    const PEEK_TOP = 1004;
    if (rowCount < 2 || rowHeights.length === 0) return DEFAULT;
    const pitch = rowHeights[0] + SPACING;
    let y = PEEK_TOP - pitch;
    if (y > DEFAULT) return DEFAULT;
    if (y < MIN) y = MIN;
    if (y + pitch >= 1080) return DEFAULT;
    return y;
  }

  /**
   * FHD design pixels → container-query width units, so the simulated TV keeps
   * the shell's 1920-wide geometry at any preview width (1cqw = 1% of the frame).
   */
  static fhd(px: number): string {
    return `${Number((px / 19.2).toFixed(4))}cqw`;
  }

  /** `#rrggbb` (or `#rgb`) → `rgba(r, g, b, alpha)`; other inputs pass through. */
  static withAlpha(hex: string, alpha: number): string {
    const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
    if (!m) return hex;
    let h = m[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const n = parseInt(h, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }

  /**
   * CSS gradient that dissolves the band edge into the page color with a
   * smoothstep curve (adr_0002: a quadratic ramp back-loaded its opacity and
   * was rejected; the outer edge is fully opaque).
   */
  static edgeMeltGradient(color: string, direction: 'to right' | 'to left'): string {
    const stops = [0, 0.25, 0.5, 0.75, 1].map((t) => {
      const alpha = 1 - t * t * (3 - 2 * t);
      return `${AppPreviewUtils.withAlpha(color, Number(alpha.toFixed(3)))} ${t * 100}%`;
    });
    return `linear-gradient(${direction}, ${stops.join(', ')})`;
  }
}
